/* eslint-disable no-unreachable */
import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import translations from 'utils/translations';
import {
  createMuiTheme,
  Grid,
  TextField,
  useMediaQuery,
  useTheme,
  withStyles,
} from '@material-ui/core';
import PageHeader from 'pages/breakout/components/PageHeader';
import { SdmTypography, SdmSpacer, SdmSelect, SdmTextButton } from 'components';
import { theme } from 'theme';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { Controller, useForm } from 'react-hook-form';
import DateFnsUtils from '@date-io/date-fns';
import { ThemeProvider } from '@material-ui/styles';
import { VisitsContext } from 'context/VisitsContextProvider';
import {
  addMinutes,
  differenceInMinutes,
  format,
  formatISO,
  setHours,
  setMinutes,
} from 'date-fns';
import { createSlot, createVisit } from 'entities/agenda/agendaHelper';
import { LoaderContext } from 'context/LoaderContextProvider';
import TimeInput from './Agenda/TimeInput/TimeInput';
import {
  AgendaVisitEndHour,
  AgendaVisitStartHour,
  SaloneEndDate,
  SaloneStartDate,
} from 'utils/constants';

const defaultValue = {
  date: undefined,
  morningStart: AgendaVisitStartHour,
  morningEnd: AgendaVisitEndHour,
  // afternoonStart: undefined,
  // afternoonEnd: undefined,
  slotTime: undefined,
  slotCapacity: undefined,
};

const calendarColor = {
  main: theme.colors.primary.red,
};

const defaultMaterialTheme = createMuiTheme({
  palette: {
    primary: calendarColor,
  },
  typography: {
    fontFamily: 'Suisse-Intl',
  },
});

const CustomFormTextField = withStyles({
  root: {
    width: '95%',
    '& label.Mui-focused': {
      color: `${theme.colors.primary.black}`,
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.colors.primary.black}`,
    },
    '& .MuiInput-underline:before': {
      borderBottom: `2px solid ${theme.colors.primary.black}`,
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Suisse-Intl',
      fontSize: 26,
    },
    '& .MuiFormLabel-root': {
      fontFamily: 'Suisse-Intl',
      color: `${theme.colors.primary.black}`,
    },
  },
})(TextField);

const CreateVisit = () => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.Agenda(intl).agenda.create;
  const history = useHistory();
  const mediaTheme = useTheme();
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));
  const { setLoaderActive } = useContext(LoaderContext);
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const { brandId, exhibitorAgenda } = useContext(VisitsContext);
  const { handleSubmit, errors, control, getValues, setValue } = useForm({
    defaultValues: defaultValue,
  });

  const [customError, setCustomError] = useState(false);

  // Constants
  const singleSlotOptions = [
    { value: 30, label: `30 ${i18n.fields.minutes}` },
    { value: 60, label: `60 ${i18n.fields.minutes}` },
    { value: 90, label: `90 ${i18n.fields.minutes}` },
  ];
  const appForSlotsOptions = [
    { value: 1, label: 1 },
    { value: 2, label: 2 },
    { value: 3, label: 3 },
    { value: 4, label: 4 },
    { value: 5, label: 5 },
    { value: 6, label: 6 },
    { value: 7, label: 7 },
    { value: 8, label: 8 },
    { value: 9, label: 9 },
    { value: 10, label: 10 },
  ];
  // Functions
  const handleBack = () => history.push('/');

  const formatInput = (data) => {
    const arrayOfKeys = [
      'morningStart',
      'morningEnd',
      // 'afternoonStart',
      // 'afternoonEnd',
    ];

    const timeRanges = arrayOfKeys.map((k) => {
      const clockTime = format(data[k], 'HH:mm');
      const _hours = clockTime.split(':')[0];
      const _minutes = clockTime.split(':')[1];
      const settedHours = setHours(data.date, parseInt(_hours));
      const settedMinutes = setMinutes(settedHours, parseInt(_minutes));
      return { label: k, value: formatISO(settedMinutes) };
    });

    const date = formatISO(data.date);
    const slotCapacity = data.slotCapacity.value;
    const slotTime = data.slotTime.value;
    return {
      slotCapacity,
      slotTime,
      visitBrandId: brandId,
      morningStart: timeRanges.find((t) => t.label === 'morningStart').value,
      morningEnd: timeRanges.find((t) => t.label === 'morningEnd').value,
      // afternoonStart: timeRanges.find((t) => t.label === 'afternoonStart')
      //   .value,
      // afternoonEnd: timeRanges.find((t) => t.label === 'afternoonEnd').value,
      date,
    };
  };

  const getFullDate = (value) => {
    const hours = value.split(':')[0];
    const minutes = value.split(':')[1];
    const dateTarget = new Date();
    dateTarget.setHours(hours);
    dateTarget.setMinutes(minutes);
    return dateTarget;
  };
  const customSubmit = async (data) => {
    const validStart = /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(
      data.morningStart
    );
    const validEnd = /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(data.morningEnd);
    if (!validStart || !validEnd) return;
    const submitStart = getFullDate(data.morningStart);
    const submitEnd = getFullDate(data.morningEnd);
    const date = new Date();
    const nineMorning = setHours(date, 9);
    const morning = setMinutes(nineMorning, 30);
    const sixAfternoon = setHours(date, 18);
    const afternoon = setMinutes(sixAfternoon, 31);
    const toEarly = submitStart < morning;
    const toLate = submitEnd > afternoon;
    if (toEarly || toLate) return setCustomError(true);
    showLoader();
    try {
      const visitAlreadySavedId = exhibitorAgenda.visits.items?.find(
        (v) => new Date(v.date).toString() === new Date(data.date).toString()
      )?.id;
      const input = formatInput({
        ...data,
        morningStart: submitStart,
        morningEnd: submitEnd,
      });
      const visitRes = visitAlreadySavedId
        ? { id: visitAlreadySavedId }
        : await createVisit({ input });
      if (visitRes && visitRes?.id) {
        const visitId = visitRes.id;
        /**Una volta creata la visit devo predisporre gli slot,
         * Il calcolo è, sia per la mattina che per il pomeriggio:
         * totale minuti (end - start) diviso il numero di minuti per slot, arrotondato per difetto
         * una volta saputo il numero di slot, ciclare:
         * per ogni iterazione creare uno slot della durata slotTime, dove startSlot è agendaStart + indexIterazione * slotTime e endSlot è startSlot + slotTime
         */

        const arrayOfCoupleKeys = [
          { start: 'morningStart', end: 'morningEnd' },
          // { start: 'afternoonStart', end: 'afternoonEnd' },
        ];

        const slotsUnParsed = arrayOfCoupleKeys.map((k) => {
          // Time available for slots in minutes
          const totalMinutes = differenceInMinutes(
            new Date(input[k.end]),
            new Date(input[k.start])
          );
          // Number of slots
          const numberOfSlots = Math.floor(
            parseFloat(totalMinutes / input.slotTime)
          );

          const array = [];

          for (let index = 0; index < numberOfSlots; index++) {
            const startDate = addMinutes(
              new Date(input[k.start]),
              index * input.slotTime
            );
            const endDate = addMinutes(new Date(startDate), input.slotTime);
            const slot = {
              date: input.date,
              slotCapacity: input.slotCapacity,
              slotVisitId: visitId,
              start: startDate,
              end: endDate,
            };
            array.push(slot);
          }
          return array;
        });
        const slots = slotsUnParsed.flat();

        const slotPromises = [];
        slots.map((s) => slotPromises.push(createSlot({ input: s })));
        const res = await Promise.all(slotPromises);
        if (res.every((r) => !!r.id)) {
          hideLoader();
          history.push('/');
        }
      }
    } catch (error) {
      console.error('LOG::  ~ error', error);
    } finally {
      hideLoader();
    }
  };

  // Renders
  const renderError = (key, labelKey) => {
    if (errors[key] && errors[key]?.type === 'required')
      return (
        <SdmTypography
          style={{ color: `${theme.colors.primary.red}`, marginTop: -15 }}
          variant={'textBold'}
        >
          {i18n.fields[labelKey ? labelKey : key].error}
        </SdmTypography>
      );
    return null;
  };
  if (!brandId) history.push('/');
  return (
    <div style={{ width: '80%', margin: '0 auto', maxWidth: 1250 }}>
      <Grid container>
        <div style={{ width: '100%' }}>
          <PageHeader
            i18n={i18n}
            isBrandPage={false}
            hasBackBtn
            onBack={handleBack}
            align={'flex-start'}
          />
        </div>
        <div
          style={{
            width: isSmDown ? '90%' : '80%',
            margin: '0 auto',
            maxWidth: 830,
          }}
        >
          <SdmTypography
            variant={isSmDown ? 'h4Regular' : 'h2Regular'}
            margin={0}
            style={{
              width: '100%',
              color: theme.colors.primary.black,
            }}
          >
            {i18n.text}
          </SdmTypography>
          <SdmSpacer height={60} />
          <SdmTypography
            variant={isSmDown ? 'h4Regular' : 'h2Regular'}
            margin={0}
            style={{
              width: '100%',
              color: theme.colors.primary.black,
            }}
          >
            {i18n.paragraph}
          </SdmTypography>
          <SdmSpacer height={60} />
          <form onSubmit={handleSubmit(customSubmit)}>
            <Grid container>
              <Grid sm={6} xs={12}>
                <SdmSpacer height={12} />
                <Controller
                  name="date"
                  control={control}
                  defaultValue={new Date(SaloneStartDate)}
                  rules={{ required: true }}
                  render={(props) => (
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <ThemeProvider theme={defaultMaterialTheme}>
                        <KeyboardDatePicker
                          format={'dd/MM/yyy'}
                          autoOk
                          value={props.value || new Date(SaloneStartDate)}
                          onChange={(e) => props.onChange(e)}
                          TextFieldComponent={CustomFormTextField}
                          id="date-required"
                          minDate={new Date(SaloneStartDate)}
                          maxDate={new Date(SaloneEndDate)}
                          label={i18n.fields.date.label}
                          disableToolbar
                        />
                      </ThemeProvider>
                    </MuiPickersUtilsProvider>
                  )}
                />
                {renderError('date')}
              </Grid>
              <Grid sm={6} xs={12}>
                <SdmTypography
                  variant={'text'}
                  margin={0}
                  style={{
                    width: '100%',
                    color: theme.colors.primary.black,
                  }}
                >
                  {i18n.fields.slotTime.label}
                </SdmTypography>
                <Controller
                  name="slotTime"
                  control={control}
                  rules={{ required: true }}
                  defaultValue={singleSlotOptions[0]}
                  render={(props) => {
                    return (
                      <SdmSelect
                        options={singleSlotOptions}
                        value={props.value || singleSlotOptions[0]}
                        labelKey={'label'}
                        label={i18n.fields.slotTime.label}
                        // disabledLabel={i18n.fields.sessionType.disabledLabel}
                        onSelect={(e) => {
                          props.onChange(e);
                        }}
                        customStyle={{ width: '95%', marginLeft: 0 }}
                        // disabled={isFormDisabled()}
                      />
                    );
                  }}
                />
                {renderError('slotTime')}
              </Grid>
              <Grid sm={6} xs={12}>
                <SdmSpacer height={isSmDown ? 20 : 40} />
                <TimeInput
                  control={control}
                  label={i18n.fields.morningStart.label}
                  theme={theme}
                  getValues={getValues}
                  setValue={setValue}
                  keyName={'morningStart'}
                  customStyle={{ width: '95%', marginLeft: 0 }}
                  isEnd={false}
                />
                {renderError('morningStart')}
                <SdmSpacer height={isSmDown ? 10 : 20} />
              </Grid>
              <Grid sm={6} xs={12}>
                <SdmSpacer height={isSmDown ? 20 : 40} />
                <TimeInput
                  control={control}
                  label={i18n.fields.morningEnd.label}
                  theme={theme}
                  getValues={getValues}
                  setValue={setValue}
                  keyName={'morningEnd'}
                  customStyle={{ width: '95%', marginLeft: 0 }}
                  isEnd={true}
                />
                {renderError('morningEnd')}
                <SdmSpacer height={isSmDown ? 10 : 20} />
              </Grid>
              <Grid sm={6} xs={12}>
                <SdmSpacer height={isSmDown ? 20 : 30} />
                <SdmTypography
                  variant={'text'}
                  margin={0}
                  style={{
                    width: '100%',
                    color: theme.colors.black,
                  }}
                >
                  {i18n.fields.slotCapacity.label}
                </SdmTypography>
                <Controller
                  name="slotCapacity"
                  control={control}
                  rules={{ required: true }}
                  defaultValue={appForSlotsOptions[0]}
                  render={(props) => {
                    return (
                      <SdmSelect
                        options={appForSlotsOptions}
                        value={props.value || appForSlotsOptions[0]}
                        labelKey={'label'}
                        label={i18n.fields.slotCapacity.label}
                        // disabledLabel={i18n.fields.sessionType.disabledLabel}
                        onSelect={(e) => {
                          props.onChange(e);
                        }}
                        customStyle={{ width: '95%', marginLeft: 0 }}
                        // disabled={isFormDisabled()}
                      />
                    );
                  }}
                />
                {renderError('slotCapacity')}
              </Grid>
              <Grid item xs={12}>
                <SdmSpacer height={isSmDown ? 20 : 40} />
                {customError && (
                  <SdmTypography
                    variant={'h4'}
                    margin={0}
                    style={{
                      width: '100%',
                      color: theme.colors.primary.red,
                    }}
                    textAlign={'right'}
                  >
                    {i18n.errors}
                  </SdmTypography>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isSmDown ? 'center' : 'flex-end',
                    marginRight: isSmDown ? 0 : 15,
                    // paddingRight: 75,
                  }}
                >
                  <SdmTextButton
                    onClick={handleBack}
                    text={i18n.buttons.cancel}
                    height={30}
                    style={{ width: 158, height: 50, marginRight: 10 }}
                    variant={'red'}
                    textVariant={'h4Regular'}
                  />
                  <SdmTextButton
                    type={'submit'}
                    text={i18n.buttons.save}
                    height={30}
                    style={{ width: 158, height: 50, marginLeft: 10 }}
                    variant={'redFill'}
                    textVariant={'h4Regular'}
                  />
                </div>
              </Grid>
            </Grid>
          </form>
        </div>
      </Grid>
      <SdmSpacer height={isSmDown ? 50 : 100} />
    </div>
  );
};
export default CreateVisit;
